import Box, { type BoxProps } from '@mui/material/Box';
import Button, { type ButtonProps } from '@mui/material/Button';
import Stack, { type StackProps } from '@mui/material/Stack';
import Typography, { type TypographyProps } from '@mui/material/Typography';
import { useTranslation } from '@tyro/i18n';
import {
  Children,
  cloneElement,
  isValidElement,
  useMemo,
  useState,
} from 'react';
import { useDisclosure } from '../../hooks';

interface LineClampContainerProps extends StackProps {
  numberOfLines: number;
  lineHeight: number;
  allowExpand?: boolean;
  TextContainerProps?: BoxProps;
  ButtonToggleProps?: ButtonProps;
  expandLabel?: string;
  collapseLabel?: string;
}

export function LineClampContainer({
  numberOfLines,
  lineHeight,
  allowExpand,
  children,
  TextContainerProps,
  ButtonToggleProps,
  expandLabel,
  collapseLabel,
  ...props
}: LineClampContainerProps) {
  const { t } = useTranslation(['common']);
  const [ref, setRef] = useState<HTMLDivElement | null>(null);
  const { isOpen, onToggle } = useDisclosure();

  const collapseHeight = lineHeight * numberOfLines;

  const isEllipsisActive = useMemo(() => {
    if (!ref) return false;

    return (
      ref.scrollHeight > ref.offsetHeight || ref.scrollHeight > collapseHeight
    );
  }, [ref, collapseHeight]);

  const expandLabelText = expandLabel ?? t('common:readMore');
  const collapseLabelText = collapseLabel ?? t('common:readLess');

  const btnTextProps = Children.toArray(children).find(
    (child) =>
      isValidElement<TypographyProps>(child) && child.type === Typography,
  );

  const buttonText = isOpen ? collapseLabelText : expandLabelText;

  return (
    <Stack alignItems="flex-start" {...props}>
      <Box
        {...TextContainerProps}
        ref={(el) => setRef(el as HTMLDivElement)}
        sx={{
          display: '-webkit-box',
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          WebkitLineClamp: isOpen ? 'initial' : numberOfLines,
          maxHeight: isOpen ? 'auto' : collapseHeight,
          ...props?.sx,
        }}
      >
        {children}
      </Box>
      {allowExpand && isEllipsisActive && (
        <Button
          variant="text"
          size="small"
          onClick={onToggle}
          {...ButtonToggleProps}
          sx={{
            py: 0,
            transform: 'translateX(-5px)',
            '&:hover': {
              backgroundColor: 'transparent',
            },
            lineHeight,
            height: 'auto',
            ...ButtonToggleProps?.sx,
          }}
        >
          {isValidElement<TypographyProps>(btnTextProps)
            ? cloneElement(btnTextProps, {
                children: buttonText,
                component: 'span',
                fontWeight: '700',
              })
            : buttonText}
        </Button>
      )}
    </Stack>
  );
}
